"use client";

import { c as _c } from "react/compiler-runtime";
import { Badge } from "@/components/radix/badge";
import { BreadcrumbItem } from "@/utils/breadcrumb-utils";
import { Home, MapPin } from "lucide-react";
import Link from "next/link";
import React from "react";
interface BreadcrumbComponentProps {
  item: BreadcrumbItem;
  isMobile: boolean;
  isDisabled?: boolean;
}
interface ClickableBreadcrumbProps extends BreadcrumbComponentProps {
  onClick: () => void;
  disabled?: boolean;
}

/**
 * Get icon for breadcrumb variant
 */
export const getIcon = (variant: string) => {
  switch (variant) {
    case "city":
      return <MapPin className="w-3 h-3 mr-1" />;
    case "default":
      return <Home className="w-3 h-3 mr-1" />;
    default:
      return null;
  }
};

/**
 * Get badge className based on mobile state, clickability, disabled and loading states
 */
export const getBadgeClassName = (isMobile: boolean, isClickable: boolean, isDisabled?: boolean) => {
  const baseClasses = "flex items-center transition-colors";
  if (isDisabled) {
    // Disabled state: muted colors, no hover effects, no cursor pointer
    const disabledClasses = "text-muted-foreground cursor-default opacity-60";
    return `${baseClasses} ${disabledClasses} ${isMobile ? "px-1 py-0.5 text-xs max-w-none" : "max-w-[100px]"}`;
  }
  if (isMobile) {
    // Mobile: no outline, minimal padding, compact design
    return `${baseClasses} ${isClickable ? "cursor-pointer hover:text-primary-700" : ""} px-1 py-0.5 text-xs max-w-none`;
  } else {
    // Desktop: keep current styling with outline
    return `${baseClasses} ${isClickable ? "cursor-pointer hover:bg-primary/10 hover:text-primary-700" : ""} max-w-[100px]`;
  }
};

/**
 * Render ellipsis breadcrumb
 */
export const EllipsisBreadcrumb: React.FC<{
  isMobile: boolean;
}> = t0 => {
  const $ = _c(2);
  const {
    isMobile
  } = t0;
  if (isMobile) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="text-xs text-muted-foreground px-1">...</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Badge variant="outline" className="cursor-default">...</Badge>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/**
 * Render navigation breadcrumb (Home)
 */
export const NavigationBreadcrumb: React.FC<BreadcrumbComponentProps> = t0 => {
  const $ = _c(7);
  const {
    item,
    isMobile
  } = t0;
  if (!("href" in item) || !item.href) {
    return null;
  }
  let t1;
  if ($[0] !== isMobile || $[1] !== item.label || $[2] !== item.variant) {
    t1 = isMobile ? <span className={getBadgeClassName(isMobile, true)}>{getIcon(item.variant || "default")}<span className="truncate">{item.label}</span></span> : <Badge variant="outline" className="hover:bg-primary/10 transition-colors cursor-pointer max-w-[100px] flex items-center">{getIcon(item.variant || "default")}<span className="truncate">{item.label}</span></Badge>;
    $[0] = isMobile;
    $[1] = item.label;
    $[2] = item.variant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== item.href || $[5] !== t1) {
    t2 = <Link href={item.href} className="hover:text-primary-700 transition-colors">{t1}</Link>;
    $[4] = item.href;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};

/**
 * Render clickable breadcrumb with custom handler
 */
export const ClickableBreadcrumb: React.FC<ClickableBreadcrumbProps> = t0 => {
  const $ = _c(22);
  const {
    item,
    isMobile,
    onClick,
    disabled: t1
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const isInteractionDisabled = disabled;
  if (isMobile) {
    const t2 = !isInteractionDisabled;
    let t3;
    if ($[0] !== isInteractionDisabled || $[1] !== isMobile || $[2] !== t2) {
      t3 = getBadgeClassName(isMobile, t2, isInteractionDisabled);
      $[0] = isInteractionDisabled;
      $[1] = isMobile;
      $[2] = t2;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    const t4 = isInteractionDisabled ? undefined : onClick;
    const t5 = item.variant || "default";
    let t6;
    if ($[4] !== t5) {
      t6 = getIcon(t5);
      $[4] = t5;
      $[5] = t6;
    } else {
      t6 = $[5];
    }
    let t7;
    if ($[6] !== item.label) {
      t7 = <span className="truncate">{item.label}</span>;
      $[6] = item.label;
      $[7] = t7;
    } else {
      t7 = $[7];
    }
    let t8;
    if ($[8] !== t3 || $[9] !== t4 || $[10] !== t6 || $[11] !== t7) {
      t8 = <span className={t3} onClick={t4}>{t6}{t7}</span>;
      $[8] = t3;
      $[9] = t4;
      $[10] = t6;
      $[11] = t7;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    return t8;
  }
  const t2 = `${isInteractionDisabled ? "text-muted-foreground cursor-default opacity-60 max-w-[100px] flex items-center" : "hover:bg-primary/10 hover:text-primary-700 transition-colors cursor-pointer max-w-[100px] flex items-center"}`;
  const t3 = isInteractionDisabled ? undefined : onClick;
  const t4 = item.variant || "default";
  let t5;
  if ($[13] !== t4) {
    t5 = getIcon(t4);
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== item.label) {
    t6 = <span className="truncate">{item.label}</span>;
    $[15] = item.label;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t2 || $[18] !== t3 || $[19] !== t5 || $[20] !== t6) {
    t7 = <Badge variant="outline" className={t2} onClick={t3}>{t5}{t6}</Badge>;
    $[17] = t2;
    $[18] = t3;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
};

/**
 * Render static breadcrumb (non-clickable)
 */
export const StaticBreadcrumb: React.FC<BreadcrumbComponentProps> = t0 => {
  const $ = _c(19);
  const {
    item,
    isMobile,
    isDisabled
  } = t0;
  if (isMobile) {
    let t1;
    if ($[0] !== isDisabled || $[1] !== isMobile) {
      t1 = getBadgeClassName(isMobile, false, isDisabled);
      $[0] = isDisabled;
      $[1] = isMobile;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const t2 = item.variant || "default";
    let t3;
    if ($[3] !== t2) {
      t3 = getIcon(t2);
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== item.label) {
      t4 = <span className="truncate">{item.label}</span>;
      $[5] = item.label;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== t1 || $[8] !== t3 || $[9] !== t4) {
      t5 = <span className={t1}>{t3}{t4}</span>;
      $[7] = t1;
      $[8] = t3;
      $[9] = t4;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  const t1 = `max-w-[100px] flex items-center ${isDisabled ? "text-muted-foreground opacity-60" : ""}`;
  const t2 = item.variant || "default";
  let t3;
  if ($[11] !== t2) {
    t3 = getIcon(t2);
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== item.label) {
    t4 = <span className="truncate">{item.label}</span>;
    $[13] = item.label;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t1 || $[16] !== t3 || $[17] !== t4) {
    t5 = <Badge variant="outline" className={t1}>{t3}{t4}</Badge>;
    $[15] = t1;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};